import "./correspondence-tab.scss";
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import { DocumentItem, FileItem } from "../../../../../models/polymer/Document.model";
import { Separator } from "../../../../separator";
import DocumentationLists from "../../../../../pages/application-coo/documents/documentation-list";
import { P2Alert, P2Button, P2ButtonGroup, P2Modal } from "react-bootstrap-common-script";
import { PlusIcon } from "../../../../../assets/svg";
import Message from "../../../../../pages/application-coo-stage-d/sections/message";
import { IErrors, useForm } from "../../../../../hooks/use-form";
import Documents from "../../../../../pages/application-coo-stage-d/sections/documents";
import DocumentationListsWithInput from "../../../../../pages/application-coo/documents/documentation-list-with-input";
import { Tab } from "../tabs-container/tabs-container";
import { SolicitorCorrespondenceResponse } from "../../../../../models/classes/solicitor/SolicitorCorrespondenceResponse.class";
import {
  SolicitorCorrespondenceAdditionalInfo,
  SolicitorCorrespondenceRequest,
} from "../../../../../models/classes/solicitor/SolicitorCorrespondenceRequest.class";
import { PolymerAxios } from "../../../../../utils/axios";
import { useAppSelector } from "../../../../../redux/hook";
import { API_Solicitor_CorrespondenceSubmit } from "../../../../../utils/api";
import { SubmitDocuments } from "../../../../../models/classes/CooSubmission.class";
import DropdownMulti from "../../../../dropdown-multi/dropdown-multi";
import { solicitor_correspondence_documents } from "../../../../../utils/shared/documents-list";
import { StageDocumentsInfo } from "../../../../../models/classes/stage-d/Review-Application.class";
import moment from "moment";
import { Helpers } from "../../../../../utils/helper";
import { uniq } from "lodash";
import { useSolicitorCaseDetailsContext } from "../../../../../context/solicitor-case-details-context";
import { v4 as uuidv4 } from "uuid";

export interface CorrespondenceTabInterface extends Tab {
  correspondenceData?: SolicitorCorrespondenceResponse;
  isSuccess: boolean;
}

const additionalDocumentCode = "SDC08";
const additionalDocsOptionDropdownLabel = solicitor_correspondence_documents?.find((item) => item.code === additionalDocumentCode)?.label ?? "";
const preliminaryDocsOptionDropdownLabel = solicitor_correspondence_documents?.find((item) => item.code === "SDC01")?.label ?? "";

export function CorrespondenceTab({ correspondenceData, isSuccess, refetch }: CorrespondenceTabInterface) {
  // States
  const queryParameters = new URLSearchParams(window.location.search);
  const applicationUuid = queryParameters.get("applicationUuid");
  const loginUser = useAppSelector((state) => state.auth.AuthenticationSlice.loginUser);
  const singpassToken = useAppSelector((state) => state.auth.AuthenticationSlice.singpassToken);
  const { uen } = useAppSelector((state) => state.auth.AuthenticationSlice.corpPassDetails);

  const [additionalDocuments, setAdditionalDocuments] = useState<DocumentItem[]>([]);
  const [submitDocsItemValues, setsubmitDocsItemValues] = useState<string[]>([]);
  const [documentSubmissionFileStorage, setDocumentSubmissionFileStorage] = useState<FileItem[]>([]);

  const [documentRequestFileMapper, setDocumentRequestFileMapper] = useState<Map<string, FileItem[]>>(new Map());

  const [additionalInfo, setAdditionalInfo] = useState<SolicitorCorrespondenceAdditionalInfo>({});

  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [submissionType, setSubmissionType] = useState<"submission" | "reply" | null>(null);
  const {
    values,
    setFormValue,
  }: { handleChange: any; values: SolicitorCorrespondenceResponse; errors: IErrors; formValid: boolean; setFormValue: (update: any) => void } = useForm(
    new SolicitorCorrespondenceResponse(correspondenceData),
    () => {},
    () => {}
  );

  const { solicitorSubmissionsRefetch } = useSolicitorCaseDetailsContext();

  // Derived values
  const numberOfSelectedItems = submitDocsItemValues.filter((itemBollean) => itemBollean && itemBollean !== additionalDocsOptionDropdownLabel).length;
  const isAdditionalInfoValid = Object.entries(additionalInfo).reduce((accumulator, [key, value]) => {
    if (key === "mortgage_payee" && value === "") return accumulator && false;
    return accumulator && true;
  }, true);

  const processedDocumentList = useMemo(() => {
    const previouslySubmittedDocuments =
      correspondenceData?.additional_document_selections?.map(
        (item) =>
          new DocumentItem({
            label: item.label,
            code: item.code,
            checkbox: false,
            checkbox_label: "",
            description: solicitor_correspondence_documents?.find((item) => item.code === additionalDocumentCode)?.description ?? "",
            sample: "",
          })
      ) ?? [];
    return solicitor_correspondence_documents
      .filter((item) => item.code !== additionalDocumentCode)
      .concat(previouslySubmittedDocuments)
      .concat(solicitor_correspondence_documents.filter((item) => item.code === additionalDocumentCode));
  }, [solicitor_correspondence_documents, correspondenceData]);

  const documentSubmissionsOptions = useMemo(() => {
    return processedDocumentList.map((document, index) => ({ id: index, title: document.label, code: document.code }));
  }, [processedDocumentList]);

  const documentsToDisplay = useMemo((): DocumentItem[] => {
    let result: DocumentItem[] = [];
    processedDocumentList?.forEach((doc) => {
      if (submitDocsItemValues.includes(doc.label) && doc.label !== additionalDocsOptionDropdownLabel) {
        //prevent additional documents being shown
        result.push(doc);
      }
    });
    return result;
  }, [processedDocumentList, submitDocsItemValues]);

  const pendingItemsCount = useMemo(() => {
    return (correspondenceData?.message_info?.length ?? 0) + (correspondenceData?.folders?.length ?? 0);
  }, [correspondenceData]);

  const latestDueDate = useMemo(() => {
    const dueDates =
      correspondenceData?.message_info
        ?.map((item) => item.due_date)
        .concat(correspondenceData?.folders?.map((item) => item.due_date))
        .filter((item) => item?.length > 0)
        .map((item) => moment(item).format("yyyy-MM-DD")) ?? [];
    const dueDatesDesc = dueDates.sort((a, b) => b.localeCompare(a));
    return dueDatesDesc.length > 0 ? dueDatesDesc[0] : moment().format("yyyy-MM-DD");
  }, [correspondenceData]);

  const isMessageValid = useMemo(() => {
    let valid = values.message_info.length > 0 && values.message_info.every((item) => item.reply && item.reply.length > 0);

    const isPartial = values.message_info.some((item) => !item?.reply) && values.message_info.some((item) => item.reply && item?.reply?.length > 0);
    const partial = valid === true ? false : isPartial;
    return { isValid: valid, isPartial: partial };
  }, [values]);

  const isDocumentsValid = useMemo(() => {
    const requiredFileLabels = values.folders.filter((e) => e.is_request_document === true).map((item) => item.name);
    const uploadedFileLabels = Array.from(documentRequestFileMapper.values())
      .flatMap((x) => x)
      .filter((item) => item.file_id !== "")
      .map((item) => item.label);

    const valid = requiredFileLabels.length > 0 && requiredFileLabels.every((item) => uploadedFileLabels.includes(item));
    const onlySomeDocumentsUploaded =
      requiredFileLabels.some((item) => uploadedFileLabels.includes(item)) && requiredFileLabels.some((item) => !uploadedFileLabels.includes(item));
    const hasFailureUpload = Array.from(documentRequestFileMapper.values())
      .flatMap((x) => x)
      .some((item) => item.file_id === "");
    const partial = valid === true ? false : onlySomeDocumentsUploaded || hasFailureUpload;
    return { isValid: valid, isPartial: partial };
  }, [values, documentRequestFileMapper]);

  const isSubmitReplyButtonEnabled = useMemo(() => {
    if (isDocumentsValid.isPartial || isMessageValid.isPartial) {
      return false;
    }

    return isDocumentsValid.isValid || isMessageValid.isValid;
  }, [isMessageValid, isDocumentsValid]);

  const isSubmitAdditionalDocumentButtonEnabled =
    isAdditionalInfoValid && documentSubmissionFileStorage.length > 0 && documentSubmissionFileStorage.every((e) => e.key !== "");

  const flattenUploadedDocuments = useMemo(() => {
    return Array.from(documentRequestFileMapper.values()).flatMap((x) => x);
  }, [documentRequestFileMapper]);

  const ModalMessage = useMemo(() => {
    const messagesCount = isMessageValid.isValid ? values.message_info.length : 0;
    const filesCount = isDocumentsValid.isValid ? flattenUploadedDocuments.length : 0;
    return (
      <div style={{ marginBottom: 24 }}>
        <p style={{ marginBottom: "0.5rem" }}>HDB will receive your submission of the following:</p>
        <ul className="polymer-reply-confirmation-modal-listing">
          {messagesCount > 0 && (
            <li style={{ marginBottom: "1rem" }}>
              {messagesCount} replied message{messagesCount > 1 ? "s" : ""}
            </li>
          )}
          {filesCount > 0 && (
            <li>
              {filesCount} submitted document{filesCount > 1 ? "s" : ""}
            </li>
          )}
        </ul>
      </div>
    );
  }, [values, isDocumentsValid, isMessageValid, flattenUploadedDocuments]);

  const documentSubmissionModalMessage = useMemo(() => {
    const filesCount = documentSubmissionFileStorage.length;
    return (
      <div style={{ marginBottom: 24 }}>
        <p style={{ marginBottom: "0.5rem" }}>HDB will receive your submission of the following:</p>
        <ul className="polymer-reply-confirmation-modal-listing">
          {filesCount > 0 && (
            <li>
              {filesCount} submitted document{filesCount > 1 ? "s" : ""}
            </li>
          )}
        </ul>
      </div>
    );
  }, [documentSubmissionFileStorage]);

  const submittingItemsCount = useMemo(() => {
    if (submissionType === "reply") {
      return (isMessageValid.isValid ? values.message_info.length : 0) + (isDocumentsValid.isValid ? values.folders.length : 0);
    } else if (submissionType === "submission") {
      return uniq(documentSubmissionFileStorage.map((item) => item.label)).length;
    } else {
      return 0;
    }
  }, [values, isDocumentsValid, isMessageValid, submissionType, documentSubmissionFileStorage]);

  // Functions
  function createSetDocumentRequestFileMapper(folderUuid: string) {
    return (files: FileItem[]) => {
      setDocumentRequestFileMapper((prev) => new Map(prev.set(folderUuid, files)));
    };
  }

  function handleAddAdditionalDocuments() {
    setAdditionalDocuments((prev) => [
      ...prev,
      new DocumentItem({
        label: ``,
        description: "Upload additional documents. Please ensure files are not password protected.",
        checkbox: false,
        checkbox_label: "",
        code: additionalDocumentCode,
        sample: "",
      }),
    ]);
  }

  const processSubmitApiData = useCallback(() => {
    const processedMessages = values.message_info.map((message) => ({
      due_date: message.due_date,
      received_date: message.received_date,
      subject: message.subject,
      message: message.message,
      reply: message.reply,
      group_uuid: message.group_uuid, //Groupd uuid
      parent_uuid: message.uuid, //existing uuid will tie to parent_uuid
      uuid: uuidv4(),
      status: message.status,
      type: message.type,
    }));

    const clonedFolders: StageDocumentsInfo[] = JSON.parse(JSON.stringify([...values.folders]));
    for (const folder of clonedFolders) {
      folder.documents = documentRequestFileMapper.get(folder.uuid)?.map((file) => {
        let fileType = "";
        if (file?.file_content?.file_name) {
          const fileNameSplitArray = file?.file_content?.file_name.split(".");
          fileType = fileNameSplitArray[fileNameSplitArray.length - 1];
        }
        return new SubmitDocuments({
          uuid: file.file_id.toString(),
          code: file.code,
          file_name: file.file_content?.file_name,
          label: file.label,
          document_type: fileType,
          required_option_checked: true,
          key: file.key,
          file_size: file.file_content.size,
          submitted_by: loginUser?.name ?? "",
        });
      });
    }

    const finalProcessedFolders = clonedFolders.filter((item: StageDocumentsInfo) => item.documents?.length !== 0);

    return new SolicitorCorrespondenceRequest(
      applicationUuid ?? "",
      loginUser.name,
      isMessageValid.isValid ? processedMessages : [],
      isDocumentsValid.isValid ? finalProcessedFolders : [],
      []
    );
  }, [documentRequestFileMapper, values, isMessageValid, isDocumentsValid, applicationUuid, loginUser]);

  const toggleModal = useCallback(
    (type?: "submission" | "reply") => () => {
      if (type) {
        setSubmissionType(type);
      }
      setIsModalOpen((prev) => !prev);
    },
    [isModalOpen]
  );

  const resetPageData = useCallback(
    (type: "reply" | "submission") => {
      refetch();
      if (type === "reply" && isDocumentsValid.isValid) {
        setDocumentRequestFileMapper(new Map());
      } else if (type === "submission") {
        setDocumentSubmissionFileStorage([]);
        setsubmitDocsItemValues([]);
        setAdditionalDocuments([]);
      }
    },
    [refetch, isDocumentsValid]
  );

  const submitReply = useCallback(async () => {
    const submitPayload = processSubmitApiData();

    try {
      await PolymerAxios(loginUser?.uinfin ?? "", singpassToken, uen).post(`${API_Solicitor_CorrespondenceSubmit}`, submitPayload);
      setIsSubmitted(true);
      resetPageData("reply");
      toggleModal()();
      solicitorSubmissionsRefetch();
    } catch (e) {
      alert("An error occurred while submitting the response. Please try again later.");
    }
  }, [processSubmitApiData, toggleModal]);

  const submitAdditionalDocuments = useCallback(async () => {
    let documentsPayload = documentSubmissionFileStorage.map((file) => {
      //NOTE: required_option_checked not track from application-step-4;
      let fileType = "";
      if (file?.file_content?.file_name) {
        const fileNameSplitArray = file?.file_content?.file_name.split(".");
        fileType = fileNameSplitArray[fileNameSplitArray.length - 1];
      }
      return new SubmitDocuments({
        uuid: file.file_id.toString(),
        code: file.code,
        file_name: file.file_content?.file_name,
        label: file.label,
        document_type: fileType,
        required_option_checked: true,
        key: file.key,
        file_size: file.file_content.size,
        submitted_by: loginUser?.name ?? "",
      });
    });

    const submitPayload = new SolicitorCorrespondenceRequest(applicationUuid ?? "", loginUser.name, [], [], documentsPayload, additionalInfo);

    try {
      await PolymerAxios(loginUser?.uinfin ?? "", singpassToken, uen).post(`${API_Solicitor_CorrespondenceSubmit}`, submitPayload);
      setIsSubmitted(true);
      resetPageData("submission");
      toggleModal()();
      solicitorSubmissionsRefetch();
    } catch (e) {
      alert("An error occurred while submitting the response. Please try again later.");
    }
  }, [applicationUuid, documentSubmissionFileStorage, submitDocsItemValues, additionalInfo, loginUser, processSubmitApiData, toggleModal]);

  const createHandleAdditionalInfo = (key: keyof SolicitorCorrespondenceAdditionalInfo) => (value: string) => {
    setAdditionalInfo((prev) => ({ ...prev, [key]: value }));
  };

  // Effects
  useEffect(() => {
    if (submitDocsItemValues.find((item) => item === additionalDocsOptionDropdownLabel) && additionalDocuments.length === 0) {
      //only add when additional documents is not added previously, i.e just check from checkbox
      handleAddAdditionalDocuments();
    }
  }, [submitDocsItemValues]);
  useEffect(() => {
    setFormValue(correspondenceData);
  }, [correspondenceData, isSuccess]);

  useEffect(() => {
    if (!correspondenceData) return;

    const initialValues: [string, FileItem[]][] = correspondenceData.folders.map((folder) => [folder.uuid, new Array<FileItem>()]);

    setDocumentRequestFileMapper(new Map(initialValues));
  }, [correspondenceData, isSuccess]);

  useEffect(() => {
    if (isSubmitted) {
      setTimeout(() => {
        setIsSubmitted(false);
        setSubmissionType(null);
      }, 1000);
    }
  }, [isSubmitted]);

  useEffect(() => {
    if (submitDocsItemValues.includes(preliminaryDocsOptionDropdownLabel)) {
      setAdditionalInfo((prev) => {
        return { ...prev, mortgage_payee: "" };
      });
    } else {
      setAdditionalInfo((prev) => {
        delete prev.mortgage_payee;
        return { ...prev };
      });
    }
  }, [submitDocsItemValues]);

  return (
    <>
      {isSuccess && (values.message_info.length !== 0 || values.folders.length !== 0) && (
        <div style={{ paddingLeft: 24, paddingRight: 24, marginBottom: 24 }}>
          <h2 className="polymer-correspondnece-tab-section__title">Reply to HDB</h2>
          <P2Alert
            withBorder={true}
            variant="warning"
            text={`${pendingItemsCount} item${pendingItemsCount > 1 ? "(s)" : ""} pending your actions. Please reply by ${Helpers.formatDate(
              latestDueDate ?? ""
            )} or the application will be cancelled.`}
          />
          {isSubmitted && submissionType === "reply" && (
            <div style={{ marginTop: 24 }}>
              <P2Alert withBorder={true} variant="success" text={"Submission has been successfully sent to HDB!"} />
            </div>
          )}
        </div>
      )}
      {isSuccess && values.message_info.length !== 0 ? (
        <>
          <Message hasSaveButton={false} value={values} setFormValue={setFormValue} sectionTitleType="badge" />
          <div style={{ paddingLeft: 24, paddingRight: 24, marginTop: 24 }}>
            <Separator variant="dashed" width={1.5} />
          </div>
        </>
      ) : null}

      {isSuccess && values.folders.length !== 0
        ? values.folders.map((folder, index) => (
            <>
              <Documents
                sectionTitleType="badge"
                folders={[folder]}
                applicationReviewSetFormValue={setFormValue}
                document_list={[]}
                filesItemsStorage={documentRequestFileMapper.get(folder.uuid)!}
                setFileItemsStorage={createSetDocumentRequestFileMapper(folder.uuid)}
                isFirstSection={false}
              />
              <Separator variant="dashed" width={1.5} style={{ marginTop: 16, marginLeft: 24, marginRight: 24 }} />
            </>
          ))
        : null}
      {(values.message_info?.length > 0 || values.folders?.length > 0) && (
        <div style={{ marginTop: 24, marginBottom: "24px" }}>
          <P2ButtonGroup>
            <P2Button onClick={toggleModal("reply")} disabled={!isSubmitReplyButtonEnabled} variant="primary-1">
              Submit
            </P2Button>
          </P2ButtonGroup>
          <Separator variant="solid" width={1.5} style={{ marginTop: "48px" }} />
        </div>
      )}

      <div style={{ paddingLeft: 24, paddingRight: 24 }}>
        <h2 className="polymer-correspondence-tab-section__title">Submit documents</h2>
        <p className="polymer-correspondence-tab-section__subtitle">
          You may submit relevant documents by selecting the prescribed document type from the dropdown menu below, or add other supporting documents. You may add up to 5 other document types
          per submission.
        </p>
        <div className="mt-2">
          <DropdownMulti
            items={documentSubmissionsOptions}
            itemValues={submitDocsItemValues}
            setItemValues={setsubmitDocsItemValues}
            label="Document type"
            placeholder="Please select type of document"
            required={true}
            style={{ marginBottom: 8 }}
            identifierKey="title"
          />
        </div>
        {numberOfSelectedItems !== 0 ? (
          <>
            <Separator variant="dashed" style={{ marginTop: 16, marginBottom: 16 }} />
            <div style={{ marginBottom: 16 }}>
              <DocumentationLists
                type={1}
                document_list={documentsToDisplay}
                filesItemsStorage={documentSubmissionFileStorage}
                setFileItemsStorage={setDocumentSubmissionFileStorage}
                getAdditionalCheckbox={() => {}}
                additionalInfo={additionalInfo}
                createHandleAdditionalInfo={createHandleAdditionalInfo}
              />
            </div>
          </>
        ) : null}
        {additionalDocuments.length !== 0 ? (
          <>
            <Separator variant="dashed" style={{ marginBottom: 16 }} />
            <div style={{ marginBottom: 16 }}>
              <DocumentationListsWithInput
                type={1}
                document_list={additionalDocuments}
                setDocuments={setAdditionalDocuments}
                filesItemsStorage={documentSubmissionFileStorage}
                setFileItemsStorage={setDocumentSubmissionFileStorage}
                getAdditionalCheckbox={() => {}}
              />
            </div>
          </>
        ) : null}
        {(submitDocsItemValues.find((item) => item === additionalDocsOptionDropdownLabel) !== undefined ||
          numberOfSelectedItems !== 0 ||
          additionalDocuments.length !== 0) && <Separator variant="dashed" />}
        {submitDocsItemValues.find((item) => item === additionalDocsOptionDropdownLabel) !== undefined && additionalDocuments.length < 5 && (
          <div style={{ display: "flex", marginBottom: 4, marginTop: 8 }}>
            <button className="polymer-correspondence-tab__button-wrapper" onClick={handleAddAdditionalDocuments}>
              <PlusIcon fillColor="#0C8188" />
              <span>Add other document type</span>
            </button>
          </div>
        )}
        {(numberOfSelectedItems !== 0 || additionalDocuments.length !== 0) && (
          <div style={{ marginTop: 24, marginBottom: 48 }}>
            <P2ButtonGroup>
              <P2Button disabled={!isSubmitAdditionalDocumentButtonEnabled} onClick={toggleModal("submission")} variant="primary-1">
                Submit
              </P2Button>
            </P2ButtonGroup>
          </div>
        )}
        {isSubmitted && submissionType === "submission" && (
          <div style={{ marginTop: 24 }}>
            <P2Alert withBorder={true} variant="success" text={"Submission has been successfully sent to HDB!"} />
          </div>
        )}
      </div>
      <P2Modal
        isOpen={isModalOpen}
        onCloseModal={toggleModal()}
        variant="default"
        title={
          <p style={{ marginBottom: 16, fontWeight: 700, fontSize: "20px" }}>{`You are submitting: ${submittingItemsCount} item${
            submittingItemsCount > 1 ? "s" : ""
          }`}</p>
        } // in future to change to .length
        primaryButtonText="No, continue editing"
        secondaryButtonText="Yes, submit to HDB"
        primaryButtonFunction={toggleModal()}
        secondaryButtonFunction={submissionType === "reply" ? submitReply : submitAdditionalDocuments}
        children={submissionType === "reply" ? ModalMessage : documentSubmissionModalMessage}
      />
    </>
  );
}
